import okaoAxios from '../helpers/axios'
import { ensureAnonId } from '../helpers/anon-id'
import { infoLog, debugLog, errorLog } from '../utils/log-helper'

export interface RewardItem {
  code: string
  name: string
  description: string
  shortDescription?: string
  usageInstructions?: string | null
  imageUrl: string | null
  pointsCost: number
  amountCents: number
  type: string
  currency: string
  cashLike: boolean
  canRedeem: boolean
  disableReason: string | null
}

export interface RewardsCatalogResponse {
  rewards: RewardItem[]
  lastUpdated: string
}

/**
 * Provisional (pre-signup) points shown to an anonymous user on the points/rewards page — the
 * "You've earned N points — sign up to claim them" signup lever. Computed by resactly-dis from the
 * device's captured anonymous activity and proxied through resactly-service (REACT_APP_SERVICE_URL).
 * The value is presentational and non-monetary until claimed on signup.
 */
export interface AnonProvisionalPointsResponse {
  provisionalPoints: number
  cap: number
  capped: boolean
  anonReviewCount: number
}

/** One itemized earning line (label + points + reason). Mirrors dis AnonPointsLineItem. */
export interface AnonPointsLineItem {
  label: string
  points: number
  reason: string
}

/** Claim response. Mirrors dis AnonPointsResponse; `welcomeBonusGranted` = the 100 credited on this call. */
export interface ClaimAnonPointsResponse extends AnonProvisionalPointsResponse {
  lineItems?: AnonPointsLineItem[]
  welcomeBonusGranted?: boolean
  /** How many anonymous saves this claim promoted into real favorites (created/reactivated). */
  savedRestaurantsMerged?: number
}

/**
 * Fetch the provisional points for an anonymous device. Hits the resactly-service `/api/points/anon`
 * proxy (which forwards to resactly-dis) — the browser never calls dis directly.
 */
export async function fetchAnonProvisionalPoints(
  anonId: string,
): Promise<AnonProvisionalPointsResponse> {
  const start = performance.now()
  infoLog('[API] fetchAnonProvisionalPoints start', {
    url: '/api/points/anon/provisional',
    method: 'GET',
  })
  try {
    const response = await okaoAxios.get<AnonProvisionalPointsResponse>(
      '/api/points/anon/provisional',
      { params: { anonId } },
    )
    const durationMs = Math.round(performance.now() - start)
    debugLog('[API] fetchAnonProvisionalPoints success', {
      status: response.status,
      durationMs,
      provisionalPoints: response.data.provisionalPoints,
    })
    return response.data
  } catch (error: any) {
    const durationMs = Math.round(performance.now() - start)
    errorLog('[API] fetchAnonProvisionalPoints failed', {
      status: error.response?.status,
      durationMs,
      error,
    })
    throw error
  }
}

/**
 * Claim-on-signup hook: tells the backend to credit (currently provisional/stubbed) the device's
 * anon points to the now-authenticated eater. Fire-and-forget from the signup flow (mirrors
 * claimAnonReviews). Non-critical: logs but does not throw.
 */
export async function claimAnonPoints(
  anonId: string,
): Promise<ClaimAnonPointsResponse | null> {
  try {
    const response = await okaoAxios.post<ClaimAnonPointsResponse>(
      '/api/points/anon/claim',
      { anonId },
    )
    return response.data ?? null
  } catch (error) {
    errorLog('Failed to claim anon points (non-critical)', error)
    return null
  }
}

/**
 * Persist an anonymous (logged-out) device's engagement action so its provisional points number
 * grows and the action credits on signup claim. Fire-and-forget, idempotent per (device,
 * restaurant, action) on the backend — safe to call on every save/scan/view. Callers must only fire
 * these for ANON users; signed-in users earn through the normal authenticated reward paths.
 *
 * Hits the resactly-service `/api/points/anon/*` proxy (which forwards to resactly-dis). The device
 * `anonId` is resolved from the never-rotated anon-id helper. Non-critical: logs but never throws.
 */
async function persistAnonAction(
  action: 'save' | 'scan' | 'view',
  restaurantId?: string | null,
  restaurantAlias?: string | null,
): Promise<void> {
  try {
    if (!restaurantId && !restaurantAlias) {
      return
    }
    const anonId = await ensureAnonId()
    if (!anonId) {
      return
    }
    await okaoAxios.post(`/api/points/anon/${action}`, {
      anonId,
      restaurantId: restaurantId ?? undefined,
      restaurantAlias: restaurantAlias ?? undefined,
    })
  } catch (error) {
    // Fire-and-forget: the existing UX flow must never break because points-persist failed.
    errorLog(`Failed to persist anon ${action} (non-critical)`, error)
  }
}

/** Persist an anon device's restaurant save (see {@link persistAnonAction}). */
export async function persistAnonSave(
  restaurantId?: string | null,
  restaurantAlias?: string | null,
): Promise<void> {
  await persistAnonAction('save', restaurantId, restaurantAlias)
}

/** Persist an anon device's menu scan (see {@link persistAnonAction}). */
export async function persistAnonScan(
  restaurantId?: string | null,
  restaurantAlias?: string | null,
): Promise<void> {
  await persistAnonAction('scan', restaurantId, restaurantAlias)
}

/** Persist an anon device's restaurant view (see {@link persistAnonAction}). */
export async function persistAnonView(
  restaurantId?: string | null,
  restaurantAlias?: string | null,
): Promise<void> {
  await persistAnonAction('view', restaurantId, restaurantAlias)
}

export interface WalletBalanceResponse {
  pointsBalance?: number | null
  walletAvailableCents?: number | null
  monthlyLimitCents?: number | null
  alreadyRedeemedCents?: number | null
  monthlyProgressPoints?: number | null
  monthlyGoalPoints?: number | null
  tier?: string | null
}

export async function fetchRewardsCatalog(): Promise<RewardsCatalogResponse> {
  const start = performance.now()
  infoLog('[API] fetchRewardsCatalog start', { url: '/api/rewards/catalog', method: 'GET' })
  
  try {
    const response = await okaoAxios.get<RewardsCatalogResponse>('/api/rewards/catalog')
    const durationMs = Math.round(performance.now() - start)
    debugLog('[API] fetchRewardsCatalog success', { status: response.status, durationMs, rewardsCount: response.data.rewards?.length ?? 0 })
    return response.data
  } catch (error: any) {
    const durationMs = Math.round(performance.now() - start)
    errorLog('[API] fetchRewardsCatalog failed', { status: error.response?.status, durationMs, error })
    throw error
  }
}

// TODO(wallet-balance-dedup): several components call this independently (MonthlyCreditHero,
// PointsSummaryCard, PointTransaction, EaterLeaderboard, EaterSubscriptionPage), so the
// points/leaderboard page fires /wallet/balance ~5x per render. Server-side caching now absorbs
// the Voucherify cost, but client dedup (one shared fetch/Redux slice) is deferred by decision
// (2026-06) to be implemented later.
export async function fetchWalletBalance(): Promise<WalletBalanceResponse> {
  const start = performance.now()
  infoLog('[API] fetchWalletBalance start', { url: '/wallet/balance', method: 'GET' })
  
  try {
    const response = await okaoAxios.get<WalletBalanceResponse>('/wallet/balance')
    const durationMs = Math.round(performance.now() - start)
    debugLog('[API] fetchWalletBalance success', {
      status: response.status,
      durationMs,
      pointsBalance: response.data.pointsBalance ?? 0,
      walletAvailableCents: response.data.walletAvailableCents ?? 0,
    })
    return response.data
  } catch (error: any) {
    const durationMs = Math.round(performance.now() - start)
    errorLog('[API] fetchWalletBalance failed', { status: error.response?.status, durationMs, error })
    throw error
  }
}

/**
 * Track restaurant share for points awarding
 * Fire-and-forget: does not throw errors, logs quietly
 */
export async function trackRestaurantShare(
  restaurantId: string,
  channel: 'facebook' | 'x' | 'instagram' | 'linkedin' | 'copy_caption' | 'copy_link',
): Promise<void> {
  try {
    await okaoAxios.post('/api/rewards/share/restaurant', {
      restaurantId,
      channel,
    })
  } catch (error) {
    // Fire-and-forget: log but don't throw
    // This is non-critical tracking, backend fraud cap handles duplicates
    const { errorLog } = await import('../utils/log-helper')
    errorLog('Failed to track restaurant share (non-critical)', error)
  }
}

